import PageHeader from "../components/PageHeader";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Check, MapPin, Phone, Mail } from "lucide-react";
import { INDUSTRIES, PRODUCT_CATEGORIES, COMPANY } from "../lib/content";

const productOptions = PRODUCT_CATEGORIES.flatMap((c) =>
  c.items.map((i) => `${c.title} — ${i}`)
);

export default function Quote() {
  const [searchParams] = useSearchParams();
  const presetIndustry = searchParams.get("industry") || "";
  const presetProduct = searchParams.get("product") || "";

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    industry: presetIndustry,
    product: presetProduct,
    quantity: "",
    destination_country: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quote", form);
      setSuccess(true);
      toast.success("Inquiry received. A trader will reply shortly.");
      setForm({
        name: "", company: "", email: "", phone: "", industry: "",
        product: "", quantity: "", destination_country: "", message: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please check your inputs.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        eyebrow="Request a Quote"
        title="Tell us what you need."
        subtitle="Submit your inquiry and a senior trader will respond with a tailored quote — usually within a few hours, across time zones."
        image="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testId="quote-page-header"
      />

      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">Headquarters</div>
              <div className="flex gap-3 text-[#0A1F3F]">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
                <p className="leading-relaxed">{COMPANY.address}</p>
              </div>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">Direct</div>
              <ul className="space-y-3 text-[#0A1F3F]">
                <li className="flex gap-3">
                  <Phone className="w-5 h-5 mt-0.5" />
                  <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-[#F37021]" data-testid="quote-phone">
                    {COMPANY.phone}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Mail className="w-5 h-5 mt-0.5" />
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-[#F37021]" data-testid="quote-email">
                    {COMPANY.email}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <div className="text-eyebrow text-[#F37021] mb-3">What to include</div>
              <ul className="space-y-2 text-sm text-[#4B5668]">
                <li>• Product specification &amp; quantity</li>
                <li>• Destination country / port</li>
                <li>• Preferred INCOTERMS</li>
                <li>• Target delivery timeline</li>
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {success ? (
              <div
                className="bg-white border border-[#E3E6EC] p-12 text-center"
                data-testid="quote-success"
              >
                <div className="w-16 h-16 bg-[#F37021] text-white flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7" />
                </div>
                <h3 className="mt-6 font-display font-black tracking-tighter text-3xl text-[#0A1F3F]">
                  Inquiry received.
                </h3>
                <p className="mt-3 text-[#4B5668]">
                  A senior trader will be in touch shortly with a tailored quote.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="quote-new-message"
                  className="mt-8 inline-flex items-center gap-3 border border-[#0A1F3F] hover:bg-[#0A1F3F] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
                >
                  Submit another inquiry
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-white border border-[#E3E6EC] p-8 sm:p-12 space-y-6"
                data-testid="quote-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="quote-field-name" />
                  <Field label="Company" name="company" value={form.company} onChange={onChange} testId="quote-field-company" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} required testId="quote-field-email" />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="quote-field-phone" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <SelectField
                    label="Industry"
                    name="industry"
                    value={form.industry}
                    onChange={onChange}
                    testId="quote-field-industry"
                    options={["", ...INDUSTRIES.map((i) => i.title)]}
                    placeholder="Select industry"
                  />
                  <SelectField
                    label="Product"
                    name="product"
                    value={form.product}
                    onChange={onChange}
                    testId="quote-field-product"
                    options={["", ...productOptions]}
                    placeholder="Select product"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Quantity" name="quantity" value={form.quantity} onChange={onChange} placeholder="e.g. 5,000 MT" testId="quote-field-quantity" />
                  <Field label="Destination Country" name="destination_country" value={form.destination_country} onChange={onChange} testId="quote-field-destination" />
                </div>
                <div>
                  <label className="text-eyebrow text-[#4B5668] mb-2 block">Message</label>
                  <textarea
                    name="message"
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    data-testid="quote-field-message"
                    className="w-full border border-[#E3E6EC] bg-[#F4F5F8] px-4 py-3 focus:outline-none focus:border-[#0A1F3F] resize-none"
                    placeholder="Specifications, timeline, INCOTERMS, additional details..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-submit-button"
                  className="inline-flex items-center gap-3 bg-[#F37021] hover:bg-[#D35F16] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required = false, testId, placeholder }) {
  return (
    <div>
      <label className="text-eyebrow text-[#4B5668] mb-2 block">
        {label} {required && <span className="text-[#F37021]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full border border-[#E3E6EC] bg-[#F4F5F8] px-4 py-3 focus:outline-none focus:border-[#0A1F3F]"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, testId, options, placeholder }) {
  return (
    <div>
      <label className="text-eyebrow text-[#4B5668] mb-2 block">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full border border-[#E3E6EC] bg-[#F4F5F8] px-4 py-3 focus:outline-none focus:border-[#0A1F3F]"
      >
        {options.map((opt, i) => (
          <option key={i} value={opt}>
            {opt || placeholder}
          </option>
        ))}
      </select>
    </div>
  );
}
